import React, { FC, useState, useEffect, ReactNode } from 'react';
import ListSubheader from '@mui/material/ListSubheader';
import translate from '@translate';
import Typography from 'src/components/Typography';
import { Divider } from '@mui/material';
import { NotificationType } from 'src/types/notification';
import StyledLink from 'src/components/StyledLink';
import { styled } from '@mui/material/styles';
import { colors, sizes } from 'src/themes/default';
import MUIMenu, { MenuProps } from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import moment from 'moment';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { OrderIcon, ReturnIcon, ClaimIcon, NotificationIcon, OnTheWayIcon } from 'src/components/SkladonIcon/BigIcons';
import { ProviderType } from 'src/types/provider';
import VritualRender from 'src/components/VritualRender';
import IconButton from 'src/components/IconButton';
import { TrashIcon } from 'src/components/SkladonIcon/SmallIcons';
import ConfirmDialog, { ConfirmDialogStatePropsType } from 'src/components/ConfirmDialog';
import Tooltip from 'src/components/Tooltip';

const Menu = styled(MUIMenu)<MenuProps>(({ theme }) => ({
    '& .MuiPaper-root': {
        width: '465px',
        borderRadius: sizes.radius,
        boxShadow: '0px 4px 8px 0px #56495929; 0px 0px 2px 0px #938B950A; 0px 1px 2px 0px #847B880F;',

        '& .MuiList-root': {
            paddingTop: 0,
            paddingBottom: 0,

            ' & .MuiListSubheader-root': {
                height: '48px',
                padding: '13px 16px',
                backgroundColor: colors.background_B2,

                '& button': { position: 'absolute', right: '0px', top: '0px', padding: '12px 16px' },
            },

            '& .MuiMenuItem-root': {
                '&:hover': {
                    backgroundColor: colors.background_B1,
                },
            },

            '& .notifications-list': {
                '& .MuiMenuItem-root': {
                    display: 'block',
                    padding: '16px',

                    '&:hover': {
                        backgroundColor: colors.background_B0,
                    },

                    '& .notifications-list-icon': {
                        display: 'inline-block',
                        height: '35px',
                        marginRight: '12px',

                        [theme.breakpoints.down('sm')]: {
                            marginRight: '0px',
                        },

                        '& svg': {
                            width: '32px',
                            height: '32px',
                            marginRight: '12px',
                        },
                    },

                    '& .notifications-list-trash': {
                        cursor: 'pointer',
                        [theme.breakpoints.down('sm')]: {
                            display: 'inline-block',
                            height: '35px',
                            marginLeft: '12px',
                            marginRight: '12px',
                            verticalAlign: 'bottom',
                        },

                        [theme.breakpoints.up('sm')]: {
                            position: 'absolute',
                            right: '8px',
                            top: '20px',
                            backgroundColor: 'inherit',
                        },

                        '&:hover svg path': {
                            fill: colors.primary_O,
                        },
                    },

                    '& .notifications-list-text-1': {
                        display: 'inline-block',
                        maxWidth: '355px',
                        height: '35px',

                        '& .MuiTypography-body1': {
                            fontWeight: sizes.bold_W,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        },
                    },

                    '& .MuiTypography-body2': {
                        whiteSpace: 'normal',
                        display: '-webkit-box',
                        WebkitLineClamp: '2',
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        width: '100%',
                        paddingTop: '8px',
                    },
                },
            },
        },
    },
}));

type NotificationsMenuPropsType = {
    anchorEl: Element | null | undefined;
    onClose: () => void;
    onDelete: (toDelete: string[]) => void;
    notificationsList: NotificationType[];
    provider: ProviderType;
    providers: ProviderType[];
    handleChangeProvider: (provider: ProviderType) => void;
};

const diffTimeFromNow = (time: string): string => {
    const timeMoment = moment(time);
    const diffDays = moment().diff(timeMoment, 'days');
    if (diffDays) {
        return translate('appBar.notificationsDays', { days: diffDays });
    }
    const diffHours = moment().diff(timeMoment, 'hours', true);
    return translate('appBar.notificationsHours', { hours: Math.floor(diffHours) });
};

type NotificatioOptionsType = {
    icon: ReactNode;
    provider?: ProviderType;
    link?: string;
};

const getProvider = (
    notification: NotificationType,
    provider: ProviderType,
    providers: ProviderType[]
): undefined | ProviderType => {
    if (notification.data.providerPublicId !== provider.PUBLIC_ID) {
        return providers.find((item) => item.PUBLIC_ID === notification.data.providerPublicId);
    }
    return undefined;
};

const getNotificationOptions = (
    notification: NotificationType,
    provider: ProviderType,
    providers: ProviderType[]
): NotificatioOptionsType => {
    const options: NotificatioOptionsType = {
        icon: <ChatBubbleOutlineIcon />,
    };

    // todo: dodat dalsie ikony notifikacii, nejsou vsechny
    switch (notification.data.NAID.split('.')[0]) {
        case 'ORDER':
            options.icon = <OrderIcon />;
            options.provider = getProvider(notification, provider, providers);
            if (notification.data.ID) {
                options.link = `/orders/${notification.data.ID}/`;
            }
            break;

        case 'CLAIM':
            options.icon = <ClaimIcon />;
            options.provider = getProvider(notification, provider, providers);
            if (notification.data.ID) {
                options.link = `/claims/${notification.data.ID}/`;
            }
            break;

        case 'RECEIVING':
            options.icon = <NotificationIcon />;
            options.provider = getProvider(notification, provider, providers);
            if (notification.data.ID) {
                options.link = `/receivings/${notification.data.ID}/`;
            }
            break;

        case 'RETURN':
            options.icon = <ReturnIcon />;
            options.provider = getProvider(notification, provider, providers);
            if (notification.data.ID) {
                options.link = `/returns/${notification.data.ID}/`;
            }
            break;

        case 'PRODUCT':
            options.icon = <NotificationIcon />;
            options.provider = getProvider(notification, provider, providers);
            if (notification.data.NAID === 'PRODUCT.TRASH_EVENT_RECORDED') {
                options.link = '/products/trash/';
            } else if (notification.data.ID) {
                options.link = `/products/${notification.data.ID}/`;
            } else {
                options.link = `/products/active/`;
            }
            break;

        case 'STOCK_TAKING':
            options.icon = <NotificationIcon />;
            options.provider = getProvider(notification, provider, providers);
            if (notification.data.ID) {
                options.link = `/stockTakings/${notification.data.ID}/`;
            }
            break;

        case 'TRANSPORT':
            options.icon = <OnTheWayIcon />;
            options.provider = getProvider(notification, provider, providers);
            if (notification.data.ID) {
                options.link = `/transports/${notification.data.ID}/`;
            }
            break;
    }

    return options;
};

const NotificationsMenu: FC<NotificationsMenuPropsType> = (props) => {
    const menuId = 'primary-noifications-menu';
    const isOpen = Boolean(props.anchorEl);

    const [confirmParams, setConfirmParams] = useState<ConfirmDialogStatePropsType | null>(null);

    useEffect(() => {
        let str = '';
        const regex = /\(.*\)/g;
        if (props.notificationsList.length) {
            str = `(${props.notificationsList.length})`;
        }
        if (document.title.match(regex)) {
            document.title = document.title.replace(/\(.*\)/, str);
        } else {
            document.title = `${document.title} ${str}`;
        }
    }, [props.notificationsList]);

    const handleClick = (provider?: ProviderType) => {
        if (provider) {
            props.handleChangeProvider(provider);
        }
    };

    const handleCloseNoticitaion = (e: React.MouseEvent<HTMLDivElement>, notification: NotificationType) => {
        e.stopPropagation();
        e.preventDefault();
        props.onDelete([notification._id]);
    };

    const renderNotifications = (isOpen: boolean) => {
        if (!isOpen) {
            return null;
        }
        return (
            <div className="notifications-list">
                <VritualRender
                    rows={props.notificationsList}
                    maxHeight={500}
                    rowId="_id"
                    renderRow={(item) => {
                        const itemData = getNotificationOptions(item, props.provider, props.providers);
                        return (
                            <StyledLink to={itemData.link}>
                                <MenuItem onClick={() => handleClick(itemData.provider)}>
                                    <div className="notifications-list-icon">{itemData.icon}</div>
                                    <Tooltip title={translate('appBar.deleteNotification')}>
                                        <div
                                            className="notifications-list-trash"
                                            onClick={(e) => handleCloseNoticitaion(e, item)}
                                        >
                                            <TrashIcon />
                                        </div>
                                    </Tooltip>
                                    <div className="notifications-list-text-1">
                                        <Typography variant="body1" showAsTitle>
                                            {item.data.title}
                                        </Typography>
                                        <Typography variant="caption" showAsTitle>
                                            {diffTimeFromNow(item.createdAt || item.data.createdAt)}
                                        </Typography>
                                    </div>
                                    <Typography variant="body2" showAsTitle>
                                        {item.data.text}
                                    </Typography>
                                    {itemData.provider && (
                                        <Typography variant="caption" showAsTitle>
                                            {itemData.provider.COMPANY_NAME}
                                        </Typography>
                                    )}
                                </MenuItem>
                            </StyledLink>
                        );
                    }}
                />
            </div>
        );
    };

    const handleClose = () => {
        props.onClose();
    };

    const handleDeleteAll = () => {
        setConfirmParams({
            title: translate('appBar.deleteAllNotificationsTitle'),
            content: translate('appBar.deleteAllNotificationsText'),
            action: () => {
                props.onDelete(props.notificationsList.map((item) => item._id));
            },
        });
    };

    return (
        <>
            <Menu
                anchorEl={props.anchorEl}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                id={menuId}
                keepMounted
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                open={isOpen}
                onClose={handleClose}
            >
                <ListSubheader>
                    <IconButton
                        noBackground
                        tooltiop={translate('appBar.deleteAllNotifications')}
                        onClick={handleDeleteAll}
                    >
                        <TrashIcon />
                    </IconButton>
                    <Typography variant="h3">
                        {translate('appBar.notifications')} ({props.notificationsList.length})
                    </Typography>
                </ListSubheader>
                <Divider />
                {renderNotifications(isOpen)}
            </Menu>
            {confirmParams && (
                <ConfirmDialog
                    title={confirmParams.title}
                    content={confirmParams.content}
                    action={confirmParams.action}
                    onClose={(): any => setConfirmParams(null)}
                />
            )}
        </>
    );
};

export default NotificationsMenu;
